/**
 * Language support.
 *
 * The codes are the backend's, not BCP 47: `default_language` is a free
 * string that the Go services copy between the employee record and each
 * workspace, and Norwegian is stored there as `no` — which is what the mobile
 * and web clients already write. Changing it here would orphan every account
 * that has already chosen a language.
 */
export const LANGUAGES = ['en', 'no', 'de'] as const;

export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'en';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

export interface LanguageOption {
  readonly code: Language;
  /** The language's name in that language, which is how people find theirs. */
  readonly label: string;
}

export const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
  { code: 'en', label: 'English' },
  { code: 'no', label: 'Norsk' },
  { code: 'de', label: 'Deutsch' },
];

/**
 * A flat dictionary: `'nav.dashboard'` rather than nested objects.
 *
 * Flat keys are what let `TranslationKey` be a finite union, so a typo in a
 * template fails the build instead of rendering the key.
 */
export type Dictionary = Readonly<Record<string, string>>;

/** Values interpolated into `{placeholder}` slots. */
export type TranslateParams = Readonly<Record<string, string | number>>;
